export const FREQUENCY_OPTIONS = [
  {
    value: 'interval',
    label: 'Every N minutes',
    summary: (s) => {
      const n = s.interval_minutes;
      if (!n) return 'Every N minutes';
      const every = `Every ${n} minute${n === 1 ? '' : 's'}`;
      const start = s.minute ?? 0;
      return `${every} from :${String(start).padStart(2, '0')}`;
    },
  },
  {
    value: 'hourly',
    label: 'Hourly',
    summary: (s) =>
      s.minute != null ? `Every hour at :${String(s.minute).padStart(2, '0')}` : 'Every hour',
  },
  {
    value: 'daily',
    label: 'Daily',
    summary: (s) =>
      s.hour != null
        ? `Daily at ${formatHourMinute(s.hour, s.minute)}`
        : 'Daily',
  },
  {
    value: 'weekly',
    label: 'Weekly',
    summary: (s) => {
      const days = (s.days_of_week || [])
        .map((d) => WEEKDAYS[d]?.short)
        .filter(Boolean)
        .join(', ');
      const time = s.hour != null ? ` at ${formatHourMinute(s.hour, s.minute)}` : '';
      return days ? `${days}${time}` : 'Weekly';
    },
  },
];

export const WEEKDAYS = [
  { value: 0, short: 'Mon', long: 'Monday' },
  { value: 1, short: 'Tue', long: 'Tuesday' },
  { value: 2, short: 'Wed', long: 'Wednesday' },
  { value: 3, short: 'Thu', long: 'Thursday' },
  { value: 4, short: 'Fri', long: 'Friday' },
  { value: 5, short: 'Sat', long: 'Saturday' },
  { value: 6, short: 'Sun', long: 'Sunday' },
];

export const INTERVAL_PRESETS = [15, 30, 60, 120, 240, 360, 720];

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export function listTimezones() {
  if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
    try {
      const zones = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length) return zones;
    } catch (e) {
      // ignore and fall back
    }
  }
  return FALLBACK_TIMEZONES;
}

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
}

export function formatHourMinute(hour, minute) {
  const h = Number(hour);
  const m = Number(minute);
  if (Number.isNaN(h) || Number.isNaN(m)) return '—';
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function summarizeSchedule(schedule) {
  if (!schedule) return 'Not scheduled';
  const opt = FREQUENCY_OPTIONS.find((o) => o.value === schedule.frequency_type);
  const summary = opt ? opt.summary(schedule) : '';
  // Frequencies that lock to a wall-clock time (daily/weekly/hourly with a
  // minute) read their time from the schedule's configured timezone, so we
  // surface that timezone alongside the summary. The browser timezone is
  // already shown next to the absolute next-run timestamp.
  const showTz = (
    schedule.timezone
    && ['daily', 'weekly', 'hourly'].includes(schedule.frequency_type)
  );
  const tzSuffix = showTz ? ` (${schedule.timezone})` : '';
  if (!schedule.enabled) return `${summary}${tzSuffix} · paused`;
  return summary ? `${summary}${tzSuffix}` : 'Scheduled';
}

export function defaultScheduleForm(tz) {
  return {
    enabled: true,
    timezone: tz || browserTimezone(),
    frequency_type: 'daily',
    interval_minutes: 60,
    hour: 2,
    minute: 0,
    days_of_week: [0, 2, 4],
    options: {},
  };
}

export function scheduleToForm(schedule, tz) {
  if (!schedule) return defaultScheduleForm(tz);
  return {
    enabled: schedule.enabled !== false,
    timezone: schedule.timezone || tz || browserTimezone(),
    frequency_type: schedule.frequency_type || 'daily',
    interval_minutes: schedule.interval_minutes ?? 60,
    hour: schedule.hour ?? 2,
    minute: schedule.minute ?? 0,
    days_of_week: Array.isArray(schedule.days_of_week)
      ? schedule.days_of_week
      : [0, 2, 4],
    options: (schedule.options && typeof schedule.options === 'object')
      ? { ...schedule.options }
      : {},
  };
}

export function formToPayload(form) {
  const base = {
    enabled: !!form.enabled,
    timezone: form.timezone || 'UTC',
    frequency_type: form.frequency_type,
    options: (form.options && typeof form.options === 'object')
      ? { ...form.options }
      : {},
  };
  if (form.frequency_type === 'interval') {
    base.interval_minutes = Number(form.interval_minutes) || 0;
    base.minute = Number(form.minute) || 0;
  } else if (form.frequency_type === 'hourly') {
    base.minute = Number(form.minute) || 0;
  } else if (form.frequency_type === 'daily') {
    base.hour = Number(form.hour) || 0;
    base.minute = Number(form.minute) || 0;
  } else if (form.frequency_type === 'weekly') {
    base.hour = Number(form.hour) || 0;
    base.minute = Number(form.minute) || 0;
    base.days_of_week = Array.isArray(form.days_of_week) ? form.days_of_week : [];
  }
  return base;
}
